import { ReactComponent as EmptyPanelDroneIcon } from '@assets/components/panel/notification/empty-panel-drone-icon.svg';
import { IcChevronBackNegative } from '@assets/icons/24x24';
import { Center, HStack, Icon, IconButton, Separator, Spinner, Text, VStack } from '@chakra-ui/react';
import { getDataColorByIndex } from '@components/ui/chart/chart.utils';
import { useAppSelector } from '@hooks/redux.hooks';
import { hasRole } from '@redux/authent/authent.selectors';
import { FormattedMessage } from 'react-intl';

import { RoleEnum } from '@/types/authent/roles.types';
import { DroneStatistics } from '@/types/data/statistic.types';

import { NotificationSearchExportButton } from '../export/NotificationSearchExportButton';
import { useNotificationSearchContext } from '../NotificationSearchContext';
import DroneByAltitudeGraph from './DroneByAltitudeGraph';
import DroneByPlatformsPie from './DroneByPlatformsPie';
import DroneDetectionByPlatformsGraph from './DroneDetectionByPlatformsGraph';
import DroneInAlertZonePie from './DroneInAlertZonePie';
import DroneOccurrenceGraph from './DroneOccurrenceGraph';
import DronesInFlightPlansPie from './DronesInFlightPlansPie';

function getPlatforms(droneStatistics?: DroneStatistics) {
  return Array.from(
    new Set(
      droneStatistics?.droneAlertsCountByDateRange
        .flatMap((entry) => entry.countByPlatform)
        .map((e) => e.platform)
        .concat(droneStatistics.dronesCountByPlatform.map((e) => e.platform)),
    ),
  ).toSorted((a, b) => a.localeCompare(b));
}

export default function NotificationSearchReporting() {
  const { setOpenedReporting, droneStatistics, isFetchingDroneStatistics } = useNotificationSearchContext();
  const platforms = getPlatforms(droneStatistics);
  function getPlatformColor(platform: string) {
    return getDataColorByIndex(platforms.indexOf(platform));
  }

  const canExport = useAppSelector((state) => hasRole(state, RoleEnum.EXPORT_NOTIFICATIONS));

  const getContent = () => {
    if (droneStatistics === undefined) {
      return (
        <Center width="100%" height="100%" backgroundColor="neutral.black" position="relative">
          <VStack gap={3}>
            <EmptyPanelDroneIcon />
            <Text
              textStyle="lg"
              fontFamily="space"
              fontFeatureSettings="'liga' off"
              color="neutral.200"
              maxWidth="230px"
              textAlign="center"
              userSelect="none"
            >
              <FormattedMessage id="global.noData" />
            </Text>
          </VStack>
        </Center>
      );
    }
    return (
      <VStack backgroundColor="neutral.900" gap={0.25} width="100%" height="100%">
        <HStack width="100%" flexShrink={0} flex={1} gap={0.25}>
          <DroneByPlatformsPie
            data={droneStatistics.dronesCountByPlatform}
            dronesCount={droneStatistics.dronesCount}
            getPlatformColor={getPlatformColor}
          />
          <DroneInAlertZonePie data={droneStatistics.droneAlertsCountByLevel} />
          <DroneDetectionByPlatformsGraph
            data={droneStatistics.droneAlertsCountByDateRange}
            getPlatformColor={getPlatformColor}
          />
        </HStack>
        <HStack width="100%" flexShrink={0} flex={1} gap={0.25}>
          <DroneByAltitudeGraph data={droneStatistics.dronesCountByAltitudeRange} />
          <DroneOccurrenceGraph data={droneStatistics.topDronesCountById} />
          <DronesInFlightPlansPie
            dronesWithFlightPlansCount={droneStatistics.dronesWithFlightPlansCount}
            dronesCount={droneStatistics.dronesCount}
          />
        </HStack>
      </VStack>
    );
  };

  return (
    <VStack
      backgroundColor="neutral.900"
      gap={0}
      height="100%"
      width="100%"
      separator={<Separator borderColor="neutral.black" size="md" />}
    >
      <HStack backgroundColor="neutral.800" gap={0} width="100%" flexShrink={0}>
        <IconButton variant="transparent" boxSize="56px" aria-label="back" onClick={() => setOpenedReporting(false)}>
          <Icon as={IcChevronBackNegative} boxSize="24px" />
        </IconButton>
        <Text color="neutral.300" marginRight="auto">
          <FormattedMessage id="components.notificationSearch.reporting.back" />
        </Text>
        {canExport && <NotificationSearchExportButton />}
      </HStack>
      {isFetchingDroneStatistics ? (
        <Center width="100%" height="100%">
          <Spinner boxSize="100px" color="neutral.200" />
        </Center>
      ) : (
        getContent()
      )}
    </VStack>
  );
}
